import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronRight, Link2 } from 'lucide-react';
import DocsHeader from '@/components/docs/DocsHeader';
import DocsFooter from '@/components/docs/DocsFooter';
import DocsToc from '@/components/docs/DocsToc';
import AppIcon from '@/components/docs/AppIcon';
import { DocsNav, DocsNavDrawer } from '@/components/docs/DocsNav';
import { DocBlockView, Inline } from '@/components/docs/DocBlocks';
import { DOCS_PATH, docNeighbours, docPath, findDoc, findGroup } from '@/data/docs/meta';
import { DOC_CONTENT } from '@/data/docs/content';
import { CONTACTS } from '@/seo/site';
import NotFound from './NotFound';

export default function DocArticle() {
  const { slug } = useParams();
  const { hash, key } = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = useCallback(() => setNavOpen(false), []);

  const meta = findDoc(slug);
  const content = meta ? DOC_CONTENT[meta.slug] : undefined;
  const sectionIds = useMemo(() => content?.sections.map((s) => s.id) ?? [], [content]);

  // ScrollToTop пропускает адреса с якорем: к разделу прокручиваем здесь,
  // в том числе при повторном клике по тому же пункту оглавления.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ block: 'start' });
  }, [slug, hash, key]);

  if (!meta || !content) return <NotFound />;

  const group = findGroup(meta.group);
  const { prev, next } = docNeighbours(meta.slug);

  return (
    <div className="min-h-screen bg-white">
      <DocsHeader onOpenNav={() => setNavOpen(true)} />

      <div className="mx-auto flex max-w-[90rem]">
        <aside className="hidden w-[17rem] shrink-0 border-r border-ink/[0.08] lg:block">
          <div className="sticky top-16 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain px-4 pb-10 pt-8">
            <DocsNav activeSlug={meta.slug} sections={content.sections} sectionsClassName="xl:hidden" />
          </div>
        </aside>

        <main className="min-w-0 flex-1 px-5 pb-24 pt-8 sm:px-8 lg:px-12 xl:px-14">
          <article className="mx-auto max-w-[46rem]">
            <nav aria-label="Хлебные крошки" className="flex flex-wrap items-center gap-1 text-[13.5px] text-ink/60">
              <Link to={DOCS_PATH} className="transition-colors hover:text-ink">
                Документация
              </Link>
              <ChevronRight aria-hidden size={14} className="text-ink/35" />
              <Link to={`${DOCS_PATH}#${group.id}`} className="transition-colors hover:text-ink">
                {group.title}
              </Link>
            </nav>

            <header className="mt-5">
              <div className="flex items-center gap-4">
                <AppIcon slug={meta.slug} size={44} />
                <h1 className="text-balance text-[clamp(1.85rem,3vw,2.5rem)] font-medium leading-[1.1] tracking-[-0.025em] text-ink">
                  {meta.title}
                </h1>
              </div>
              <p className="mt-5 text-[17.5px] leading-[1.65] text-ink/70">
                <Inline text={content.lead} />
              </p>
              <dl className="mt-8 grid overflow-hidden rounded-xl border border-ink/[0.09] sm:grid-cols-3">
                {content.facts.map((fact) => (
                  <div
                    key={fact.label}
                    className="border-t border-ink/[0.08] px-4 py-3 first:border-t-0 sm:border-l sm:border-t-0 sm:first:border-l-0"
                  >
                    <dt className="text-[12.5px] text-ink/60">{fact.label}</dt>
                    <dd className="mt-1 text-[14.5px] font-medium leading-snug text-ink">
                      <Inline text={fact.value} />
                    </dd>
                  </div>
                ))}
              </dl>
            </header>

            {/* Оглавление внутри статьи нужно там, где нет правой колонки. */}
            <details className="group mt-8 rounded-xl border border-ink/[0.09] lg:hidden [&_summary::-webkit-details-marker]:hidden">
              <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-[14.5px] font-medium text-ink">
                На этой странице
                <ChevronRight
                  aria-hidden
                  size={16}
                  className="text-ink/45 transition-transform duration-200 group-open:rotate-90"
                />
              </summary>
              <ul className="border-t border-ink/[0.08] px-2 py-2">
                {content.sections.map((section) => (
                  <li key={section.id}>
                    <Link
                      to={`#${section.id}`}
                      className="block rounded-lg px-2 py-1.5 text-[14px] text-ink/70 transition-colors hover:bg-ink/[0.035] hover:text-ink"
                    >
                      {section.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </details>

            {content.sections.map((section) => (
              <section key={section.id} aria-labelledby={section.id} className="mt-14">
                <div className="group flex items-center gap-1.5">
                  <h2
                    id={section.id}
                    className="scroll-mt-24 text-[1.5rem] font-medium leading-snug tracking-[-0.02em] text-ink"
                  >
                    {section.title}
                  </h2>
                  <Link
                    to={`#${section.id}`}
                    aria-label={`Ссылка на раздел «${section.title}»`}
                    className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink/35 opacity-0 transition-opacity hover:bg-ink/5 hover:text-ink focus-visible:opacity-100 group-hover:opacity-100"
                  >
                    <Link2 size={16} strokeWidth={2} />
                  </Link>
                </div>
                {section.blocks.map((block, i) => (
                  <DocBlockView key={i} block={block} />
                ))}
              </section>
            ))}

            <nav aria-label="Соседние статьи" className="mt-16 grid gap-3 border-t border-ink/[0.08] pt-8 sm:grid-cols-2">
              {prev ? (
                <Link
                  to={docPath(prev.slug)}
                  className="group rounded-xl border border-ink/[0.09] px-5 py-4 transition-colors duration-200 hover:border-ink/25"
                >
                  <span className="text-[13px] text-ink/60">Назад</span>
                  <span className="mt-1 flex items-center gap-2 text-[15.5px] font-medium text-ink">
                    <ArrowLeft
                      aria-hidden
                      size={16}
                      strokeWidth={2}
                      className="shrink-0 text-ink/45 transition-transform duration-200 group-hover:-translate-x-0.5"
                    />
                    {prev.title}
                  </span>
                </Link>
              ) : (
                <span aria-hidden className="hidden sm:block" />
              )}
              {next ? (
                <Link
                  to={docPath(next.slug)}
                  className="group rounded-xl border border-ink/[0.09] px-5 py-4 text-right transition-colors duration-200 hover:border-ink/25"
                >
                  <span className="text-[13px] text-ink/60">Дальше</span>
                  <span className="mt-1 flex items-center justify-end gap-2 text-[15.5px] font-medium text-ink">
                    {next.title}
                    <ArrowRight
                      aria-hidden
                      size={16}
                      strokeWidth={2}
                      className="shrink-0 text-ink/45 transition-transform duration-200 group-hover:translate-x-0.5"
                    />
                  </span>
                </Link>
              ) : null}
            </nav>

            <p className="mt-10 text-[14.5px] leading-relaxed text-ink/65">
              Не нашли ответ? Напишите нам в{' '}
              <a
                href={CONTACTS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 underline decoration-blue-700/30 underline-offset-[3px] transition-colors hover:decoration-blue-700"
              >
                WhatsApp
              </a>{' '}
              или на{' '}
              <a
                href={`mailto:${CONTACTS.supportEmail}`}
                className="text-blue-700 underline decoration-blue-700/30 underline-offset-[3px] transition-colors hover:decoration-blue-700"
              >
                {CONTACTS.supportEmail}
              </a>
              .
            </p>
          </article>
        </main>

        <aside className="hidden w-[15.5rem] shrink-0 xl:block">
          <div className="sticky top-16 max-h-[calc(100dvh-4rem)] overflow-y-auto py-10 pr-6">
            <DocsToc sections={content.sections} ids={sectionIds} />
          </div>
        </aside>
      </div>

      <DocsFooter />

      {navOpen && <DocsNavDrawer activeSlug={meta.slug} sections={content.sections} onClose={closeNav} />}
    </div>
  );
}
